import { Link } from "react-router-dom";

const INSTRUCTOR_PHOTO =
  "https://storage.readdy-site.link/project_files/39dbbb86-a257-4ad3-8c48-92d4e85cea90/6dba3717-63d1-496f-8e1e-700d5d68a755_image_1773456420574.jpg?v=257a8ac25fe02c67c1aa31747ff2467f";

const NATHANIEL_PHOTO =
  "https://public.readdy.ai/ai/img_res/edited_7a89f4dd0b1108434df44def4d8b32ff_d8851023.jpg";

const team = [
  {
    name: "Destiny",
    role: "Registered Nurse • BLS / ACLS / PALS Instructor",
    bio: "With hands-on clinical experience in ICU and emergency settings, our founder created Cardiocraft CPR to bring hospital-grade life support training to Kissimmee. Every class reflects real-world nursing expertise — not just textbook theory.",
    img: INSTRUCTOR_PHOTO,
    quote: "My mission is to make sure every student leaves here confident, certified, and ready to save a life.",
    tags: ["Registered Nurse", "AHA Aligned", "Same-Day Cert", "ICU / ER Experience"],
    badge: "Founder",
    featured: true,
  },
  {
    name: "Nathaniel",
    role: "Registered Nurse • BLS / Heartsaver CPR Instructor",
    bio: "Nathaniel brings years of frontline nursing experience to every Cardiocraft CPR class. His calm, clear instruction style makes even the most technical life-support protocols approachable for every student — from first-timers to seasoned healthcare professionals.",
    img: NATHANIEL_PHOTO,
    quote: "Every skill I teach comes straight from the bedside — because that\u2019s where it counts.",
    tags: ["Registered Nurse", "AHA Aligned", "BLS / Heartsaver", "Clinical Expert"],
    badge: "RN Instructor",
    featured: true,
  },
];

const values = [
  { icon: "ri-nurse-line", title: "Medical Expertise", desc: "Every instructor is a credentialed RN with real-world clinical experience." },
  { icon: "ri-heart-pulse-line", title: "Life-Saving Mission", desc: "We believe access to quality CPR training can change — and save — lives." },
  { icon: "ri-medal-line", title: "AHA Standards", desc: "All courses strictly follow American Heart Association protocols and guidelines." },
  { icon: "ri-hand-heart-line", title: "Hands-On Focus", desc: "Theory meets practice. Our classes emphasize real skill, not just certificates." },
];

const SITE_URL = import.meta.env.VITE_SITE_URL || '';

export default function AboutPage() {
  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'EducationalOrganization',
        name: 'Cardiocraft CPR',
        url: `${SITE_URL}/about`,
        description: 'RN-led CPR and life support certification in Kissimmee, FL. Founded by Registered Nurses with ICU and ER experience.',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '1627 East Vine Street, Suite 203',
          addressLocality: 'Kissimmee',
          addressRegion: 'FL',
          postalCode: '34744',
          addressCountry: 'US',
        },
        employee: [
          {
            '@type': 'Person',
            name: 'Destiny',
            jobTitle: 'Registered Nurse & CPR Instructor',
            description: 'Founder of Cardiocraft CPR with hands-on clinical experience in ICU and emergency settings. AHA-aligned BLS, ACLS, and PALS instructor.',
          },
          {
            '@type': 'Person',
            name: 'Nathaniel',
            jobTitle: 'Registered Nurse & CPR Instructor',
            description: 'Registered Nurse instructor specializing in BLS and Heartsaver CPR courses with frontline nursing experience.',
          },
        ],
      },
    ],
  };

  return (
    <main className="pt-20">
      <title>About Cardiocraft CPR | RN Instructors Kissimmee FL | Destiny & Nathaniel</title>
      <meta name="description" content="Meet the Registered Nurses behind Cardiocraft CPR in Kissimmee, FL. Destiny and Nathaniel bring real ICU and ER experience to every BLS, ACLS, and PALS class. Learn our story." />
      <meta name="keywords" content="CPR instructors Kissimmee, RN CPR trainer, Cardiocraft CPR about, BLS instructor Florida" />
      <link rel="canonical" href={`${SITE_URL}/about`} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />
      {/* Hero */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/edited_16e29e16b1bf76ec38448bdfd7e2cf2c_eeb98bec.jpg"
            alt="About Cardiocraft CPR Kissimmee"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-purple-dark/90 via-brand-purple/75 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-2xl">
            <span className="inline-block border border-brand-gold/50 text-brand-gold text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full mb-6">
              Our Story
            </span>
            <h1 className="font-serif text-5xl lg:text-6xl font-bold text-white mb-6">
              Training Led by Nurses <span className="text-brand-gold">Who Know What&apos;s at Stake</span>
            </h1>
            <p className="text-white/80 text-lg leading-relaxed mb-8">
              Cardiocraft CPR was founded on a simple belief: CPR training should be taught
              by people who have actually used these skills to save lives.
            </p>
            <Link
              to="/booking#reserve-spot"
              className="inline-flex items-center gap-2 bg-brand-gold text-brand-black font-bold px-8 py-4 rounded-md hover:bg-brand-gold-dark transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-calendar-check-line" />
              Book a Class Today
            </Link>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block bg-brand-purple-pale text-brand-purple text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full mb-4">
                Our Mission
              </span>
              <h2 className="font-serif text-4xl font-bold text-brand-black mb-6">
                Life-Saving Education Delivered with{" "}
                <span className="text-brand-purple">Clinical Precision</span>
              </h2>
              <p className="text-gray-600 leading-relaxed mb-5">
                Cardiocraft CPR was founded by <strong className="text-brand-purple">Registered Nurses</strong> who
                experienced firsthand the difference that proper CPR training makes in real emergencies. We started
                this business because we believed Kissimmee deserved better — a training center where every student
                walks away truly prepared.
              </p>
              <p className="text-gray-600 leading-relaxed mb-5">
                We offer{" "}
                <strong className="text-brand-purple">American Heart Association–aligned</strong> courses taught with
                the clinical depth and situational awareness that only comes from real-world nursing experience.
                Our instructors don&apos;t just read slides — they share the scenarios, the urgency, and the expertise
                that comes from working in hospitals and emergency rooms.
              </p>
              <p className="text-gray-600 leading-relaxed mb-8">
                Located in the heart of Kissimmee, FL at 1627 East Vine Street, Suite 203, we serve individuals,
                healthcare teams, businesses, and community organizations — with same-day classes and certification
                always available.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/courses" className="bg-brand-purple text-white font-semibold px-6 py-3 rounded-md hover:bg-brand-purple-dark transition-colors cursor-pointer whitespace-nowrap text-sm">
                  View Our Courses
                </Link>
                <Link to="/contact" className="border-2 border-brand-purple text-brand-purple font-semibold px-6 py-3 rounded-md hover:bg-brand-purple hover:text-white transition-colors cursor-pointer whitespace-nowrap text-sm">
                  Contact Us
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img
                src="https://public.readdy.ai/ai/img_res/5cc58fd772ef838780c8d1b8acc08a91.jpg"
                alt="Destiny demonstrating CPR compressions to students"
                className="rounded-xl object-cover object-top w-full h-64"
              />
              <img
                src="https://readdy.ai/api/search-image?query=male%20Black%20nurse%20instructor%20in%20black%20scrubs%20kneeling%20beside%20Hispanic%20female%20student%20correcting%20hand%20placement%20on%20CPR%20training%20mannequin%20hands-on%20coaching%20moment%20realistic%20candid%20medical%20training%20room%20natural%20warm%20lighting&width=300&height=340&seq=about-scene2-v2&orientation=portrait"
                alt="Nathaniel assisting student with CPR hand placement"
                className="rounded-xl object-cover object-top w-full h-64 mt-8"
              />
              <img
                src="https://readdy.ai/api/search-image?query=small%20diverse%20group%20of%20three%20adults%20Black%20and%20Hispanic%20man%20and%20woman%20practicing%20AED%20pad%20placement%20on%20training%20mannequin%20together%20step%20by%20step%20instructor%20visible%20in%20background%20modern%20CPR%20training%20room%20realistic%20candid%20photograph&width=300&height=240&seq=about-scene3-v2&orientation=landscape"
                alt="Students practicing AED use in Kissimmee CPR class"
                className="rounded-xl object-cover object-top w-full h-44"
              />
              <img
                src="https://readdy.ai/api/search-image?query=wide%20shot%20CPR%20certification%20classroom%20multiple%20diverse%20Black%20and%20Hispanic%20adult%20students%20practicing%20chest%20compressions%20on%20mannequins%20simultaneously%20RN%20instructor%20observing%20and%20walking%20between%20tables%20natural%20overhead%20lighting%20realistic%20community%20training%20environment%20candid%20group%20photo&width=300&height=240&seq=about-scene4-v2&orientation=landscape"
                alt="Students actively practicing CPR while instructor observes"
                className="rounded-xl object-cover object-top w-full h-44 mt-8"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-brand-purple-pale">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="font-serif text-4xl font-bold text-brand-black mb-4">
              Our Core Values
            </h2>
            <div className="w-20 h-1 bg-brand-gold mx-auto" />
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v) => (
              <div key={v.title} className="bg-white rounded-xl p-6 border border-brand-purple/10 text-center">
                <div className="w-14 h-14 flex items-center justify-center bg-brand-purple rounded-xl text-white mx-auto mb-4">
                  <i className={`${v.icon} text-2xl`} />
                </div>
                <h3 className="font-semibold text-brand-black text-base mb-2">{v.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="font-serif text-4xl font-bold text-brand-black mb-4">
              Meet Our RN Instructors
            </h2>
            <div className="w-20 h-1 bg-brand-gold mx-auto mb-4" />
            <p className="text-gray-500 max-w-xl mx-auto">
              Our instructors are credentialed Registered Nurses with years of hands-on clinical experience.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {team.map((member) => (
              <div
                key={member.name}
                className="rounded-2xl overflow-hidden transition-all duration-500 hover:-translate-y-1 group cursor-pointer"
                style={{
                  background: "linear-gradient(135deg, #1a0a2e 0%, #0d0d1a 100%)",
                  border: "1px solid rgba(212,175,55,0.25)",
                }}
              >
                {/* Photo */}
                <div className="relative h-80 overflow-hidden">
                  <img
                    src={member.img}
                    alt={`${member.name} — RN Instructor Cardiocraft CPR`}
                    className="w-full h-full object-cover object-top transition-all duration-700 group-hover:scale-105 group-hover:brightness-110"
                  />
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background:
                        "linear-gradient(to top, rgba(13,9,26,0.92) 0%, rgba(13,9,26,0.2) 60%, transparent 100%)",
                    }}
                  />
                  {/* Purple glow overlay on hover */}
                  <div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                    style={{ background: "radial-gradient(ellipse at center bottom, rgba(107,70,193,0.35) 0%, transparent 70%)" }}
                  />
                  {/* Gold border shimmer on hover */}
                  <div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-t-2xl"
                    style={{ boxShadow: "inset 0 0 0 1px rgba(212,175,55,0.6)" }}
                  />
                  {/* AHA badge */}
                  <div
                    className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 rounded-full"
                    style={{
                      background: "linear-gradient(135deg, #6B46C1, #4C1D95)",
                      border: "1px solid rgba(212,175,55,0.5)",
                    }}
                  >
                    <i className="ri-shield-check-fill text-brand-gold text-xs" />
                    <span className="text-white text-xs font-bold">AHA Instructor</span>
                  </div>
                  {/* Role badge top right */}
                  <div
                    className="absolute top-4 right-4 px-3 py-1.5 rounded-full"
                    style={{
                      background: "rgba(212,175,55,0.12)",
                      border: "1px solid rgba(212,175,55,0.4)",
                      backdropFilter: "blur(8px)",
                    }}
                  >
                    <span className="text-brand-gold text-xs font-bold">{member.badge}</span>
                  </div>
                  {/* Name over bottom */}
                  <div className="absolute bottom-0 left-0 right-0 px-6 pb-4">
                    <h3 className="font-serif font-bold text-xl text-white mb-0.5">{member.name}</h3>
                    <p className="text-brand-gold text-xs font-semibold uppercase tracking-wide">{member.role}</p>
                  </div>
                </div>

                {/* Content */}
                <div className="px-6 py-5">
                  <blockquote
                    className="pl-4 italic text-white/70 text-sm leading-relaxed mb-4"
                    style={{ borderLeft: "2px solid rgba(212,175,55,0.6)" }}
                  >
                    &ldquo;{member.quote}&rdquo;
                  </blockquote>
                  <p className="text-white/55 text-sm leading-relaxed mb-4">{member.bio}</p>
                  <div className="flex flex-wrap gap-2">
                    {member.tags.map((tag) => (
                      <span
                        key={tag}
                        className="text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap"
                        style={{
                          background: "rgba(107,70,193,0.2)",
                          border: "1px solid rgba(107,70,193,0.4)",
                          color: "rgba(255,255,255,0.85)",
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-brand-purple">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-serif text-3xl font-bold text-white mb-4">
            Ready to Learn from the Best in Kissimmee?
          </h2>
          <p className="text-white/80 mb-8">
            Same-day CPR certification taught by Registered Nurses. Book your spot now.
          </p>
          <Link
            to="/booking#reserve-spot"
            className="inline-flex items-center gap-2 bg-brand-gold text-brand-black font-bold px-8 py-4 rounded-md hover:bg-brand-gold-dark transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-calendar-check-line" />
            Book a Class
          </Link>
        </div>
      </section>
    </main>
  );
}
